import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { EventContentArg } from '@fullcalendar/core'

import { EventModality } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { ProviderCalendarBaseSlotSelectionModal } from './ProviderCalendarBaseSlotSelectionModal'
import {
  ProviderCalendarDaySlotSelectionContent,
  ProviderCalendarDaySlotSelectionContentRef,
} from '../providerCalendarSlotSelectionContent/ProviderCalendarDaySlotSelectionContent'

export type ProviderCalendarDaySlotSelectionModalProps = {
  addresses?: Address[] | null
  currentEvents?: { startTime: string; endTime: string; modality?: EventModality | null }[]
  defaultVideoPlatform?: VIDEO_PLATFORM | null
  eventToEdit?: EventContentArg | null
  flexibleAvailabilityWindows?: boolean
  inPersonPreference?: boolean
  splitVideoOptions?: boolean
  onConfirmationButtonPress: (events: { startTime: string; endTime: string; modality?: EventModality | null }[]) => void
  onRequestClose: () => void
  rangeToAdd?: { start: string; end: string; startLocal: string; endLocal: string } | null
  setShowAddEventModal: (showModal: boolean) => void
  showAddEventModal: boolean
  showModalitySelection?: boolean
  slotMaxTime?: string
  slotMinTime?: string
  videoPreference?: boolean
  isNPSession?: boolean
  /** Days to hide from day selection dropdown (0=Sunday, 6=Saturday) */
  hiddenDays?: number[]
}

export const ProviderCalendarDaySlotSelectionModal: FunctionComponent<ProviderCalendarDaySlotSelectionModalProps> = ({
  addresses,
  currentEvents = [],
  defaultVideoPlatform,
  eventToEdit,
  flexibleAvailabilityWindows,
  inPersonPreference,
  splitVideoOptions,
  onConfirmationButtonPress,
  onRequestClose,
  rangeToAdd,
  setShowAddEventModal,
  showAddEventModal,
  showModalitySelection,
  slotMaxTime = '22:00:00',
  slotMinTime = '07:00:00',
  videoPreference,
  isNPSession,
  hiddenDays,
}) => {
  const { formatMessage } = useIntl()
  const contentRef = useRef<ProviderCalendarDaySlotSelectionContentRef>(null)
  const [isValid, setIsValid] = useState<boolean>(false)

  const confirmSelection = () => {
    const events = contentRef.current?.buildPayload() ?? []
    setShowAddEventModal(false)
    onConfirmationButtonPress(events)
  }

  // Track whether the modal is currently open so the close-animation callback
  // (onDismiss) doesn't reset state when the modal has already reopened.
  const visibleRef = useRef(showAddEventModal)
  useEffect(() => {
    visibleRef.current = showAddEventModal
  }, [showAddEventModal])

  const handleAnimationEnd = () => {
    if (!visibleRef.current) {
      contentRef.current?.reset()
    }
  }

  return (
    <ProviderCalendarBaseSlotSelectionModal
      visible={showAddEventModal}
      onRequestClose={onRequestClose}
      handleAnimationEnd={handleAnimationEnd}
      onConfirmationButtonPress={confirmSelection}
      modalTitle={
        eventToEdit
          ? formatMessage({
              defaultMessage: 'Edit recurring availability',
              description: 'Title of modal to update an availabilty',
            })
          : formatMessage({
              defaultMessage: 'Add recurring availability',
              description: 'Title of modal to add an availabilty',
            })
      }
      cancelButtonText={formatMessage({
        defaultMessage: 'Cancel',
        description: 'Button to cancel creating or editing event time and date',
      })}
      confirmationButtonText={
        eventToEdit
          ? formatMessage({ defaultMessage: 'Save', description: 'Button to save new event time and date' })
          : formatMessage({ defaultMessage: 'Add', description: 'Button to add new event time and date' })
      }
      confirmButtonDisabled={!isValid}
      modalContents={
        <ProviderCalendarDaySlotSelectionContent
          ref={contentRef}
          addresses={addresses}
          currentEvents={currentEvents}
          defaultVideoPlatform={defaultVideoPlatform}
          eventToEdit={eventToEdit}
          flexibleAvailabilityWindows={flexibleAvailabilityWindows}
          inPersonPreference={inPersonPreference}
          splitVideoOptions={splitVideoOptions}
          rangeToAdd={rangeToAdd}
          showModalitySelection={showModalitySelection}
          slotMaxTime={slotMaxTime}
          slotMinTime={slotMinTime}
          videoPreference={videoPreference}
          onValidityChange={setIsValid}
          isNPSession={isNPSession}
          hiddenDays={hiddenDays}
        />
      }
      testID={'ProviderCalendarDaySlotSelectionModal'}
    />
  )
}
